import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { toast } from "sonner";
import { Loader2, Send, Mail, Phone, MapPin, MessageSquare } from "lucide-react";

export default function ContactPage() {
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const { data: settings = {} } = useSiteSettings();

  const contactEmail = settings.contact_email || "info@allsale.co.nz";
  const contactPhone = settings.contact_phone;
  const whatsapp = settings.whatsapp;
  const address = settings.address || "Auckland, Aotearoa · New Zealand";

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/contact", {
        name: f.name.trim(),
        email: f.email.trim(),
        phone: f.phone.trim() || undefined,
        subject: f.subject.trim() || undefined,
        message: f.message.trim(),
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Thanks! We'll get back to you soon.");
      setF({ name: "", email: "", phone: "", subject: "", message: "" });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Couldn't send — please try again."),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    mut.mutate();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Contact us" description="Reach the Allsale Classified team — sales, support and feedback." />
      <Breadcrumbs items={[{ label: "Contact us" }]} />

      <div className="mt-6 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-1 space-y-5">
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">Contact us</h1>
          <p className="text-slate-500">Kia Ora! We usually reply within one business day.</p>
          <div className="space-y-3 text-sm text-slate-600">
            <a href={`mailto:${contactEmail}`} className="inline-flex items-start gap-2 hover:text-[color:var(--brand-blue)] transition-colors" data-testid="contact-info-email">
              <Mail className="w-4 h-4 mt-0.5 text-[color:var(--brand-blue)]" /> {contactEmail}
            </a>
            {contactPhone && (
              <a href={`tel:${contactPhone.replace(/\s+/g, "")}`} className="inline-flex items-start gap-2 hover:text-[color:var(--brand-blue)] transition-colors" data-testid="contact-info-phone">
                <Phone className="w-4 h-4 mt-0.5 text-[color:var(--brand-blue)]" /> {contactPhone}
              </a>
            )}
            {whatsapp && (
              <a
                href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-start gap-2 hover:text-[color:var(--brand-blue)] transition-colors"
                data-testid="contact-info-whatsapp"
              >
                <MessageSquare className="w-4 h-4 mt-0.5 text-emerald-500" /> WhatsApp: {whatsapp}
              </a>
            )}
            <div className="inline-flex items-start gap-2" data-testid="contact-info-address"><MapPin className="w-4 h-4 mt-0.5 text-[color:var(--brand-blue)]" /> {address}</div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="md:col-span-2 rounded-2xl bg-white shadow-card p-6 md:p-8 space-y-4">
          <Field label="Your name *">
            <input data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="input" />
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Email *">
              <input data-testid="contact-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="input" />
            </Field>
            <Field label="Phone (optional)">
              <input data-testid="contact-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input" />
            </Field>
          </div>
          <Field label="Subject">
            <input data-testid="contact-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="input" />
          </Field>
          <Field label="Message *">
            <textarea data-testid="contact-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} required className="input min-h-40" />
          </Field>
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={mut.isPending}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold py-3 disabled:opacity-70 transition-colors"
          >
            {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send message
          </button>
        </form>
      </div>

      <style>{`.input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:14px;padding:.7rem 1rem;font-size:.95rem;color:var(--text-primary);}
      .input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}
      textarea.input{padding-top:.75rem;padding-bottom:.75rem;line-height:1.5;}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
